import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMyProfile, refreshMyYoutubeMemberships, updateMyProfile } from '../api/fan-api';
import { getStoredFanClaims } from '../auth/fan-auth';
import { FanProfileForm } from './FanProfileForm';

async function loadProfileWithRetry(): Promise<Awaited<ReturnType<typeof getMyProfile>>> {
  // First SSO login can race the API authorizer cache — token issued
  // a moment ago might 401 on the first GET. Retry with backoff so
  // we don't permanently sink the modal into 'idle' on a transient
  // error.
  const delays = [0, 800, 2200];
  let lastErr: unknown = null;
  for (const d of delays) {
    if (d > 0) await new Promise((r) => setTimeout(r, d));
    try {
      return await getMyProfile();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
}

/**
 * Blocking full-screen modal shown the first time a signed-in fan
 * arrives without a `profileCompletedAt` stamp on their fan profile
 * row. Covers both cases the operator asked for:
 *   1. brand-new fan that just confirmed email → row self-heals on
 *      first GET /api/fan/me with profileCompletedAt undefined → modal
 *   2. legacy fan that signed up before this feature shipped → row
 *      exists but profileCompletedAt undefined → modal
 *
 * displayName is required; birthday + gender are optional per the
 * operator's call. Submit hits PUT /api/fan/me; backend stamps
 * profileCompletedAt server-side so the modal never reappears.
 *
 * Rendered at the Router level so it overlays every route including
 * the chrome-less chat route.
 *
 * The form body is reused via <FanProfileForm /> — same fields
 * appear in the Profile-page edit sheet (iter 8 onwards), so any
 * change to the form (a new field, validation tweak) lands in both
 * surfaces automatically.
 */
type AsyncState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'visible'; initial: { displayName: string; birthday?: string; gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say' } }
  | { kind: 'done' };

export function FanProfileSetupModal() {
  // Re-evaluate on EVERY route change: this component mounts at the
  // Router level BEFORE sign-in, and an in-app navigation (the Google
  // OAuth callback's `navigate('/')`) does NOT re-render it on its own
  // — so `signedIn` stayed stale-false until a full page refresh
  // (tester report 2026-07-13:「重新整理後才突然跑出來」). Subscribing
  // to the router location makes the post-login navigation itself the
  // trigger.
  const location = useLocation();
  const signedIn = Boolean(getStoredFanClaims()?.sub);
  const [state, setState] = useState<AsyncState>({ kind: 'idle' });
  // Once resolved (done/visible) for this signed-in session, don't
  // refetch the profile on every subsequent route change.
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!signedIn) {
      resolvedRef.current = false;
      setState({ kind: 'idle' });
      return;
    }
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    let alive = true;
    setState({ kind: 'loading' });
    // Fire-and-forget YT membership refresh once per signed-in mount.
    // Covers all login paths (Google SSO callback + native SRP) since
    // this modal renders at the router level on every route.
    if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem('nx_yt_refreshed')) {
      sessionStorage.setItem('nx_yt_refreshed', '1');
      void refreshMyYoutubeMemberships();
    }
    (async () => {
      try {
        const profile = await loadProfileWithRetry();
        if (!alive) return;
        // Show the modal if profile setup is incomplete OR displayName
        // is blank (federated SSO users don't trigger PostConfirmation,
        // so the self-heal row lands with an empty name and the user
        // needs to confirm it before the SPA moves on).
        const needsSetup = !profile.profileCompletedAt || !profile.displayName?.trim();
        if (!needsSetup) {
          setState({ kind: 'done' });
          return;
        }
        // Pre-fill from JWT name claim (Google sends `name` on the
        // id_token via the `profile` scope) when the DDB row has
        // nothing useful. The form is editable so the user can still
        // change it before submit.
        const claims = getStoredFanClaims();
        const fallbackName = claims?.name?.trim() ?? '';
        const initialName =
          (profile.displayName?.trim()) || fallbackName || '';
        setState({
          kind: 'visible',
          initial: {
            displayName: initialName,
            ...(profile.birthday ? { birthday: profile.birthday } : {}),
            ...(profile.gender ? { gender: profile.gender } : {}),
          },
        });
      } catch {
        // After retries failed — reset the latch so the NEXT route
        // change re-runs the check (previous comment claimed a remount
        // happens; it doesn't — this component never unmounts).
        if (alive) { resolvedRef.current = false; setState({ kind: 'idle' }); }
      }
    })();
    return () => { alive = false; };
  }, [signedIn, location.pathname]);

  if (state.kind !== 'visible') return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fan-profile-setup-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl p-6 sm:p-8 space-y-5">
        <div>
          <h2 id="fan-profile-setup-title" className="text-xl font-semibold text-slate-100">
            完善你的個人資料
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            這是初次登入時的一次性設定。暱稱必填，其他可略。
          </p>
        </div>
        <FanProfileForm
          initial={state.initial}
          submitLabel="完成設定"
          onSubmit={async (values) => {
            await updateMyProfile(values);
            setState({ kind: 'done' });
          }}
        />
      </div>
    </div>
  );
}
